// The URLs of a session from the platform's pages, as the platform's apps have theirs (/email/<account id>/document/
// <id>): /c5mail/<platform account id>/campaigns/<emailid>/content and so on. They're always under /c5mail, where the
// platform's domain serves C5 Mail, which is where every such session comes from.
//
// A page is {name: 'campaigns'}, {name: 'contentgroup', categoryname}, {name: 'campaign', emailid, tab ('content' or
// 'launch', ManageCampaignPage's routes)}, {name: 'lists'} or {name: 'list', listid}.

export const PLATFORM_BASE_PATH = '/c5mail';

const itemIdPattern = /^[0-9a-f]{8}$/;
const platformAccountIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The session's platform account id, in lowercase as the platform's URLs and tokens have it
export function getSessionPlatformAccountId() {
  return window.c5platformaccount.platform_id.toLowerCase();
}

export function getPlatformPath(page) {
  let base = PLATFORM_BASE_PATH + '/' + getSessionPlatformAccountId();

  switch (page.name) {
    case 'campaigns':
      return base + '/campaigns';

    case 'contentgroup':
      return base + '/campaigns/groups/' + encodeURIComponent(page.categoryname);

    case 'campaign':
      return base + '/campaigns/' + page.emailid + '/' + (page.tab ?? 'content');

    case 'lists':
      return base + '/lists';

    case 'list':
      return base + '/lists/' + page.listid;
  }

  throw new Error('Unknown page ' + page.name);
}

// The page at a path (location.pathname): null for a path that isn't a platform page's (/c5mail/ after the SSO,
// /c5mail/open/...), else {platformAccountId (lowercase), page}. page is null for a path that only stands for the
// default page (the account's own, or one that isn't a page, as the platform's apps send their unknown paths to their
// overview), and {name: 'notfound'} for an item that can't be (an id that isn't one, or a path that doesn't decode).
// .../campaigns/<emailid> is the campaign's content.
export function parsePlatformPath(pathname) {
  if (!pathname.startsWith(PLATFORM_BASE_PATH + '/')) {
    return null;
  }

  let parts = pathname.slice(PLATFORM_BASE_PATH.length + 1).split('/');
  if (parts[parts.length - 1] === '') {
    parts.pop();
  }

  if (parts.length === 0 || !platformAccountIdPattern.test(parts[0])) {
    return null;
  }

  let platformAccountId = parts[0].toLowerCase();

  let rest;
  try {
    // Each part on its own, as a content group's name can have an encoded /
    rest = parts.slice(1).map((part) => decodeURIComponent(part));
  } catch (e) {
    return {platformAccountId: platformAccountId, page: {name: 'notfound'}};
  }

  let page = null;

  if (rest[0] === 'campaigns') {
    if (rest.length === 1) {
      page = {name: 'campaigns'};
    } else if (rest[1] === 'groups' && rest.length === 3 && rest[2] !== '') {
      page = {name: 'contentgroup', categoryname: rest[2]};
    } else if (rest.length === 2 || (rest.length === 3 && (rest[2] === 'content' || rest[2] === 'launch'))) {
      page = itemIdPattern.test(rest[1]) ? {name: 'campaign', emailid: rest[1], tab: rest[2] ?? 'content'} : {name: 'notfound'};
    }
  } else if (rest[0] === 'lists') {
    if (rest.length === 1) {
      page = {name: 'lists'};
    } else if (rest.length === 2) {
      page = itemIdPattern.test(rest[1]) ? {name: 'list', listid: rest[1]} : {name: 'notfound'};
    }
  }

  return {platformAccountId: platformAccountId, page: page};
}
